// The exported code uses Tailwind CSS. Install Tailwind CSS in your dev environment to ensure all styles work.
import React, { useState,useEffect,useRef,useMemo } from 'react';

type Compound = {
  key: "urolithinA" | "luteolin" | "spermidine";
  name: string;
  subtitle: string;
  origin: string;
  category: string;
  benefitTitle: string;
  benefitBody: string;
  imageUrl: string;
  accent: string;
};

export function RevionyxCompoundsShowcase({ contained = true }: { contained?: boolean }) {
  const compounds: Compound[] = useMemo(
    () => [
      {
        key: "urolithinA",
        name: "Pomegranate-Derived Postbiotic Metabolite",
        subtitle: "Rich in Urolithin A",
        origin: "Derived from pomegranate polyphenols via microbial metabolism",
        category: "Rich in Urolithin A",
        benefitTitle: "Cellular energy & vitality support",
        benefitBody:
          "Supports healthy cellular energy processes and helps maintain mitochondrial quality.",
        imageUrl: "urolithina.png",
        accent: "from-[#ACD7DE]/20 via-[#ACD7DE]/10 to-transparent",
      },
      {
        key: "luteolin",
        name: "Plant Flavonoid",
        subtitle: "Rich in Luleolin",
        origin: "Botanical flavonoid found in many plant sources",
        category: "Rich in Luteolin",
        benefitTitle: "Oxidative balance & resilience",
        benefitBody:
          "Helps support the body’s natural antioxidant defenses and healthy inflammatory balance.",
        imageUrl: "luteolin.png",
        accent: "from-[#ACD7DE]/20 via-[#ACD7DE]/10 to-transparent",
      },
      {
        key: "spermidine",
        name: "Wheat-Germ Derived Polyamine",
        subtitle: "Rich in Spermidine",
        origin: "A naturally occurring molecule present in living cells",
        category: "Rich in Spermidine",
        benefitTitle: "Cell maintenance & Integrity pathways",
        benefitBody:
          "Supports normal cellular maintenance processes and helps support healthy aging at a cellular level.",
        imageUrl: "spermidine.png",
        accent: "from-emerald-200/20 via-emerald-100/10 to-transparent",
      },
    ],
    []
  );

  const [activeKey, setActiveKey] = useState<Compound["key"]>("urolithinA");
  const active = compounds.find((c) => c.key === activeKey) ?? compounds[0];
  const detailsRef = useRef<HTMLDivElement | null>(null);
  const [shouldScrollToDetails, setShouldScrollToDetails] = useState(false);

  const isMobile = () =>
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(max-width: 767px)").matches;
  
  useEffect(() => {
    if (!shouldScrollToDetails) return;
    if (!isMobile()) {
      setShouldScrollToDetails(false);
      return;
    }

    const t = setTimeout(() => {
      const el = detailsRef.current;
      if (!el) return;

      const offset = 88; // sticky nav height buffer
      const top = window.scrollY + el.getBoundingClientRect().top - offset;

      window.scrollTo({ top, behavior: "smooth" });
      setShouldScrollToDetails(false);
    }, 50);

    return () => clearTimeout(t);
  }, [activeKey, shouldScrollToDetails]);


  return (
    <section className="bg-white border-b border-gray-100 py-7 md:py-9">
      <div className={contained ? "max-w-6xl mx-auto px-6" : "w-full"}>
        {/* Compact header */}
        <div className="flex items-center gap-3 mb-3">
          <span className="inline-flex items-center rounded-full border border-[#C7A64C]/25 bg-[#FFFDF5] px-3 py-1 text-[11px] font-semibold tracking-[0.18em] uppercase text-[#7B6A3B]">
            Revionyx™ Core Actives
          </span>
          <span className="hidden sm:inline text-[12px] text-gray-400 tracking-wide">
            science-driven • nature-powered
          </span>
        </div>

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-5">
          <h3 className="text-xl md:text-2xl font-bold text-[#121212] tracking-tight">
            Major Components, Designed to Work Together
          </h3>
          <p className="text-sm text-gray-600 md:max-w-[520px] leading-relaxed">
            Three core bioactives—built for consistency, safety, and daily ritual.
          </p>
        </div>

        {/* Compact layout */}
        <div className="md:hidden">
        <div className="text-[11px] text-gray-500 mb-3">Scroll to explore each active</div>

        {/* snap container */}
        <div className="space-y-4 snap-y snap-mandatory">
          {compounds.map((c) => (
            <section
              key={c.key}
              className="
                snap-start
                rounded-2xl border border-gray-200/70 bg-white
                shadow-[0_14px_48px_rgba(0,0,0,0.06)]
                overflow-hidden
              "
            >
              <div className={`h-2 w-full bg-gradient-to-r ${c.accent}`} />

              <div className="p-4">
                <div className="flex items-start gap-4">
                  <div className="relative w-20 h-20 rounded-xl overflow-hidden border border-gray-200 flex-shrink-0 bg-gray-50">
                    <img
                      src={c.imageUrl}
                      alt={c.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>

                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-[0.22em] text-gray-500 font-semibold">
                      Core Active
                    </div>
                    <div className="text-lg font-bold text-[#121212] tracking-tight">
                      {c.name}
                    </div>
                    <div className="text-sm text-gray-700 mt-0.5">{c.subtitle}</div>
                  </div>
                </div>

                <div className="grid gap-3 mt-4">
                  <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-3">
                    <div className="text-xs font-semibold text-[#121212] mb-1">What it is</div>
                    <p className="text-xs text-gray-600 leading-relaxed">{c.origin}</p>
                  </div>

                  <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-3">
                    <div className="text-xs font-semibold text-[#121212] mb-1">Why it matters</div>
                    <div className="text-xs font-bold text-[#121212]">{c.benefitTitle}</div>
                    <p className="text-xs text-gray-600 leading-relaxed mt-1">{c.benefitBody}</p>
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-gray-100">
                  <p className="text-[11px] leading-relaxed text-gray-500">
                    *Informational only. Consult your veterinarian for your pet’s needs.
                  </p>
                </div>
              </div>
            </section>
          ))}
        </div>
      </div>



      <div className="hidden md:grid md:grid-cols-[1fr_1.4fr] gap-6 items-stretch">
      {/* Left: compact vertical index */}
      <div className="rounded-2xl border border-gray-200/70 bg-white shadow-sm p-3">
        <div className="text-sm font-semibold text-[#121212] mb-2">Active Index</div>
        <div className="text-[11px] text-gray-500 mb-3">Hover or tap to explore</div>

        <div className="space-y-2">
          {compounds.map((c) => {
            const isActive = c.key === activeKey;
            return (
              <button
                key={c.key}
                type="button"
                onMouseEnter={() => setActiveKey(c.key)}
                onFocus={() => setActiveKey(c.key)}
                onClick={() => {
                  setActiveKey(c.key);
                  setShouldScrollToDetails(true);
                }}
                className={[
                  "w-full text-left px-3 py-2 rounded-lg border transition-all duration-200",
                  "hover:border-gray-300 hover:bg-gray-50",
                  isActive
                    ? "border-[#acd7de]/40 bg-[#FFFDF5] shadow-[0_6px_18px_rgba(199,166,76,0.12)]"
                    : "border-gray-200 bg-white",
                ].join(" ")}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span
                      className={[
                        "h-2 w-2 rounded-full",
                        isActive ? "bg-[#acd7de]" : "bg-gray-300",
                      ].join(" ")}
                    />
                    <div className="text-sm font-semibold text-[#121212]">{c.name}</div>
                  </div>
                  <span className="text-gray-300">→</span>
                </div>

                <div className="mt-0.5 text-[11px] text-gray-600">{c.category}</div>
              </button>
            );
          })}
        </div>

        <div className="mt-3 text-[11px] text-gray-500 leading-relaxed">
          *Informational only. Consult your veterinarian for your pet’s needs.
        </div>
       </div>

          {/* Right: compact details */}
          <div
            ref={detailsRef}
            className="rounded-2xl border border-gray-200/70 bg-white shadow-[0_14px_48px_rgba(0,0,0,0.06)] overflow-hidden"
          >
            {/* Subtle top tint */}
            <div className={`h-2 w-full bg-gradient-to-r ${active.accent}`} />

            <div className="p-4 md:p-5">
              <div className="flex items-start gap-4">
                {/* Small image (no tall hero banner) */}
                <div className="relative w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden border border-gray-200 flex-shrink-0 bg-gray-50">
                  <img
                    src={active.imageUrl}
                    alt={active.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>

                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-gray-500 font-semibold">
                    Core Active
                  </div>
                  <div className="text-lg md:text-xl font-bold text-[#121212] tracking-tight">
                    {active.name}
                  </div>
                  <div className="text-sm text-gray-700 mt-0.5">{active.subtitle}</div>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3 mt-4">
                <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-3">
                  <div className="text-xs font-semibold text-[#121212] mb-1">What it is</div>
                  <p className="text-xs text-gray-600 leading-relaxed">{active.origin}</p>
                </div>

                <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-3">
                  <div className="text-xs font-semibold text-[#121212] mb-1">
                    Why it matters
                  </div>
                  <div className="text-xs font-bold text-[#121212]">{active.benefitTitle}</div>
                  <p className="text-xs text-gray-600 leading-relaxed mt-1">
                    {active.benefitBody}
                  </p>
                </div>
              </div>

              <div className="mt-4 pt-3 border-t border-gray-100">
                <p className="text-[11px] leading-relaxed text-gray-500">
                  *These statements have not been evaluated by the U.S. Food and Drug Administration.
                  This product is not intended to diagnose, treat, cure, or prevent any disease.
                  For animal use only.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const App: React.FC = () => {
const [email, setEmail] = useState('');
const [isScrolled, setIsScrolled] = useState(false);

const [mobileOpen, setMobileOpen] = useState(false);

useEffect(() => {
  // lock body scroll when menu is open
  document.body.style.overflow = mobileOpen ? "hidden" : "";
  return () => {
    document.body.style.overflow = "";
  };
}, [mobileOpen]);

const goTo = (href: string) => {
  setMobileOpen(false);

  // internal anchor on the same page
  if (href.startsWith("#")) {
    requestAnimationFrame(() => {
      const el = document.querySelector(href);
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return;
  }

  // normal navigation
  window.location.href = href;
};


const [hoveredSize, setHoveredSize] = useState<string | null>(null);
useEffect(() => {
const handleScroll = () => {
setIsScrolled(window.scrollY > 50);
};
window.addEventListener('scroll', handleScroll);
return () => window.removeEventListener('scroll', handleScroll);
}, []);
const handleEmailSubmit = (e: React.FormEvent) => {
e.preventDefault();
// Handle email submission
console.log('Email submitted:', email);
};
const videoRef = useRef<HTMLVideoElement | null>(null);

useEffect(() => {
  const video = videoRef.current;
  if (!video) return;

  // Always start muted and paused
  video.muted = true;
  video.pause();
  video.currentTime = 0;

  // Define the observer
  let hasPlayedOnce = false;
  const observer = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];
      if (entry.isIntersecting && entry.intersectionRatio > 0.35) {
        // play only when visible
        video.play().catch(() => {});
        hasPlayedOnce = true;
      } else {
        // pause only if it has played before
        if (hasPlayedOnce) video.pause();
      }
    },
    {
      root: null,
      threshold: [0, 0.25, 0.35, 0.5, 0.75, 1],
      rootMargin: '-20% 0px -20% 0px', // ensures trigger only when centered
    }
  );

  // Attach observer AFTER layout is stable
  const timeout = setTimeout(() => observer.observe(video), 800);

  // Cleanup
  return () => {
    clearTimeout(timeout);
    observer.disconnect();
  };
}, []);


  useEffect(() => {
  const scrollToHash = () => {
    const hash = window.location.hash;
    if (!hash) return;
    const el = document.querySelector(hash);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // first load
  const t = setTimeout(scrollToHash, 50);

  // if hash changes while on the page
  window.addEventListener("hashchange", scrollToHash);

  return () => {
    clearTimeout(t);
    window.removeEventListener("hashchange", scrollToHash);
  };
}, []);


return (
<div className="min-h-screen bg-white" style={{ fontFamily: 'Inter, sans-serif' }}>
{/* Navigation */}
<nav className={`sticky top-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/98 backdrop-blur-md shadow-sm' : 'bg-white'} border-b border-gray-100`}>
<div className="max-w-6xl mx-auto px-6 py-4">
<div className="flex items-center justify-between">
<a href="/" className="flex items-center">
  <img
    src="/logo.svg"
    alt="Together Ever Logo"
    className="h-5 md:h-6 w-auto"
  />
</a>
<div className="hidden md:flex items-center space-x-8">
<a href="/" className="text-sm font-medium text-gray-600 hover:text-[#121212] transition-colors cursor-pointer uppercase tracking-wide">Home</a>
<a href="/science" className="text-sm font-medium text-gray-600 hover:text-[#121212] transition-colors cursor-pointer uppercase tracking-wide">Science</a>
<a href="/about" className="text-sm font-medium text-gray-600 hover:text-[#121212] transition-colors cursor-pointer uppercase tracking-wide">About</a>
<button className="bg-[#121212] text-white px-6 py-1 text-sm font-medium rounded-button hover:bg-gray-800 transition-all duration-300 whitespace-nowrap cursor-pointer uppercase tracking-wide">
Join Waitlist
</button>
</div>

{/* Mobile controls */}
      <div className="flex items-center gap-3 md:hidden">
        {/* Hamburger */}
        <button
          type="button"
          aria-label="Open menu"
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((v) => !v)}
          className="h-9 w-9 grid place-items-center rounded-lg hover:bg-gray-50 active:bg-gray-100"
        >
          <span className="block w-5">
            <span className={`block h-[2px] bg-gray-800 transition-all ${mobileOpen ? "translate-y-[6px] rotate-45" : ""}`} />
            <span className={`block h-[2px] bg-gray-800 mt-[5px] transition-all ${mobileOpen ? "opacity-0" : ""}`} />
            <span className={`block h-[2px] bg-gray-800 mt-[5px] transition-all ${mobileOpen ? "-translate-y-[8px] -rotate-45" : ""}`} />
          </span>
        </button>
      </div>
    </div>
  </div>

  {/* Mobile menu panel */}
  <div className={`md:hidden ${mobileOpen ? "block" : "hidden"}`}>
    {/* backdrop */}
    <div
      className="fixed inset-0 bg-black/20"
      onClick={() => setMobileOpen(false)}
    />

    {/* sheet */}
    <div
  className="
    fixed top-[64px] left-0 right-0
    rounded
    bg-white/55 backdrop-blur-xl backdrop-brightness-110 backdrop-saturate-150
    border border-white/40
    shadow-[0_18px_60px_rgba(0,0,0,0.16)]
    overflow-hidden
  "
>
  <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/35 via-white/10 to-transparent" />
      <div className="relative px-4 py-4 space-y-2">
        <button
          onClick={() => goTo("/")}
          className="w-full text-left px-3 py-3 rounded-xl hover:bg-gray-50 active:bg-gray-100 text-sm font-semibold text-[#121212]"
        >
          Home
        </button>
        <button
          onClick={() => goTo("/science")}
          className="w-full text-left px-3 py-3 rounded-xl hover:bg-gray-50 active:bg-gray-100 text-sm font-semibold text-[#121212]"
        >
          Science
        </button>
        <button
          onClick={() => goTo("/about")}
          className="w-full text-left px-3 py-3 rounded-xl hover:bg-gray-50 active:bg-gray-100 text-sm font-semibold text-[#121212]"
        >
          About
        </button>
</div>
</div>
</div>
</nav>

<h1 className="sr-only">
  Together Ever | Longevity & Healthy Aging Solutions for Dogs & Cats
</h1>

{/* Hero Section */}
<section id="hero" className="relative flex items-center justify-center overflow-hidden bg-white pt-20 md:pt-24 px-4 md:px-10 -mt-1 md:-mt-3">
  {/* Video Background Container (narrower + centered) */}
  <div
    className="
      absolute top-3 bottom-5 left-4 right-4
      md:top-4 md:bottom-6
      md:left-8 md:right-8
      lg:left-0 lg:right-0
      mx-auto
      lg:max-w-[1120px] xl:max-w-[1250px]
      bg-gradient-to-br from-[#FAF9F5] via-[#D7E1C7]/30 to-[#C7A64C]/20
      rounded-3xl overflow-hidden
    "
  >
    <video
      autoPlay
      muted
      loop
      playsInline
      className="
        absolute inset-0 w-full h-full
        object-cover md:object-cover
        object-[center_22%]
        md:scale-[1.02]
      "
    >
      <source src="together.mp4" type="video/mp4" />
      Your browser does not support the video tag.
    </video>

    {/* Overlay */}
    <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-black/15 to-transparent z-0"></div>
  </div>

  {/* Content */}
  <div
    className="
      relative z-10
      max-w-6xl mx-auto px-6
      grid md:grid-cols-2 gap-8 md:gap-10
      items-center
      min-h-[72vh] md:min-h-[66vh]
      py-10 md:py-12
    "
  >
    {/* Left */}
    <div className="md:pl-6 lg:pl-10 md:-translate-y-6 lg:-translate-y-8 transform">
      <div className="mb-5">
        <span className="inline-block bg-red-500 text-white px-3 py-1 text-xs font-bold uppercase tracking-wide !rounded-button">
          Powered by Science
        </span>
      </div>

      <h2
        className="text-3xl md:text-4xl font-bold mb-5 leading-[1.08] tracking-tight text-white drop-shadow-lg"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        Make The Good Days Last
      </h2>

      <p className="text-base md:text-lg mb-7 leading-relaxed max-w-lg text-white/95 drop-shadow-lg">
        A longevity system rooted in science, made to support healthy aging and vitality in your dogs and cats
      </p>

      <button
        onClick={() => goTo("#start")}
        className="
          px-7 py-2.5 text-sm font-medium text-white uppercase tracking-wide rounded-lg
          bg-white/30 backdrop-blur-md backdrop-brightness-110
          shadow-[0_4px_20px_rgba(255,255,255,0.25)]
          hover:bg-white/50 transition-all duration-300
        "
      >
        Start Longevity Journey →
      </button>

      <div className="mt-9 pt-6 border-t border-white/20 text-left">
        <span className="text-sm text-white font-medium">
          Rich in{" "}
          <span className="font-semibold bg-gradient-to-r from-[#C7A64C] to-[#D4B55A] bg-clip-text text-transparent">
            Revionyx™
          </span>{" "}
          | Supports healthy aging
        </span>
      </div>
    </div>

    {/* Right (badge) */}
    <div className="relative hidden md:block min-h-[42vh]">
      <div className="absolute bottom-8 right-2 px-4 py-3 rounded-lg bg-white/30 backdrop-blur-md backdrop-brightness-110 shadow-[0_4px_20px_rgba(255,255,255,0.25)]">
        <div className="flex items-center space-x-2">
          <span className="w-3 h-3 bg-green-500 rounded-full"></span>
          <div>
            <div className="text-xs font-bold text-[#121212]">THE FIRST LONGEVITY SUPPLEMENT</div>
            <div className="text-xs text-gray-700">FOR OUR BEST FRIENDS</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

{/* As Featured On */}
<section className="bg-white py-4 overflow-hidden">
  <div className="max-w-6xl mx-auto px-6">
    <div className="mx-auto lg:max-w-[1100px] xl:max-w-[1200px]">
      <div className="h-px w-full bg-gray-100 mb-4" />

      <div className="flex flex-col items-center gap-3">
        <div className="text-[11px] tracking-[0.22em] uppercase text-gray-400">
          As Featured On
        </div>

        <div className="relative w-full overflow-hidden featured-fade">
          <div className="featured-marquee-track">
            {[
              {
                name: "Forbes",
                src: "/forbes_logo.png",
                href: "https://forbesliechtenstein.com/together-ever-ignites-a-new-frontier-in-pet-longevity-with-science-first-aging-innovations/",
                clickable: true,
                alt: "Forbes logo",
              },
              {
                name: "Business Insider",
                src: "/Business_Insider_Logo.png",
                href: "https://markets.businessinsider.com/news/currencies/together-ever-introduces-revionyx-to-support-healthy-aging-in-premium-pet-nutrition-1035935583",
                clickable: true,
                alt: "Business Insider logo",
              },
              {
                name: "Pet Food Processing",
                src: "/Pet_Food_Processing_logo.png",
                href: "https://www.petfoodprocessing.net/articles/20341-together-ever-introduces-healthy-aging-ingredient-platform-for-pet-nutrition",
                clickable: true,
                alt: "Pet Food Processing logo",
              },
              {
                name: "Yahoo Finance",
                src: "/Yahoo_Finance_logo.png",
                href: "",
                clickable: false,
                alt: "Yahoo Finance logo",
              },
              {
                name: "AP News",
                src: "/AP_Logo.png",
                href: "",
                clickable: false,
                alt: "AP News logo",
              },

              // duplicate set for seamless loop
              {
                name: "Forbes",
                src: "/forbes_logo.png",
                href: "https://forbesliechtenstein.com/together-ever-ignites-a-new-frontier-in-pet-longevity-with-science-first-aging-innovations/",
                clickable: true,
                alt: "Forbes logo",
              },
              {
                name: "Business Insider",
                src: "/Business_Insider_Logo.png",
                href: "https://markets.businessinsider.com/news/currencies/together-ever-introduces-revionyx-to-support-healthy-aging-in-premium-pet-nutrition-1035935583",
                clickable: true,
                alt: "Business Insider logo",
              },
              {
                name: "Pet Food Processing",
                src: "/Pet_Food_Processing_logo.png",
                href: "https://www.petfoodprocessing.net/articles/20341-together-ever-introduces-healthy-aging-ingredient-platform-for-pet-nutrition",
                clickable: true,
                alt: "Pet Food Processing logo",
              },
              {
                name: "Yahoo Finance",
                src: "/Yahoo_Finance_logo.png",
                href: "",
                clickable: false,
                alt: "Yahoo Finance logo",
              },
              {
                name: "AP News",
                src: "/AP_Logo.png",
                href: "",
                clickable: false,
                alt: "AP News logo",
              },
            ].map((item, idx) => {
              const logoContent = (
                <div
                  className={[
                    "featured-logo-item",
                    item.clickable ? "cursor-pointer" : "cursor-default",
                  ].join(" ")}
                  aria-label={item.name}
                  title={item.clickable ? `Open ${item.name}` : item.name}
                >
                  <img
                    src={item.src}
                    alt={item.alt}
                    className={[
                      "featured-logo-img",
                      item.clickable ? "featured-logo-clickable" : "featured-logo-static",
                    ].join(" ")}
                    loading="lazy"
                    draggable={false}
                  />
                </div>
              );

              return item.clickable ? (
                <a
                  key={`${item.name}-${idx}`}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="featured-logo-link"
                >
                  {logoContent}
                </a>
              ) : (
                <div
                  key={`${item.name}-${idx}`}
                  className="featured-logo-link"
                  aria-disabled="true"
                >
                  {logoContent}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="h-px w-full bg-gray-100 mt-4" />
    </div>
  </div>
</section>


{/* Research Foundation */}
<section id="start" className="py-12 pb-0 bg-white border-b border-gray-100">
<div className="max-w-6xl mx-auto px-6">
<div className="text-center mb-16">
<h2
className="text-2xl md:text-3xl font-medium text-[#121212] mb-6 leading-relaxed md:leading-relaxed tracking-relaxed"
style={{ fontFamily: 'Inter, sans-serif' }}
>
Time to support your pet’s cellular vitality with<br />
<span className="bg-gradient-to-r from-[#acd7de] to-[#acd7de] text-white px-1.5 py-1 text-2xl md:text-4xl !rounded-button">Revionyx™</span><br />
Our proprietary natural longevity complex that<br />
supports cellular longevity, energy, and metabolism.
</h2>
<div className="mt-12">
<button
 onClick={() => goTo("/science")}
 className="border-2 border-[#121212] text-[#121212] px-6 py-3 text-sm font-medium !rounded-button hover:bg-[#121212] hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer uppercase tracking-wide">
Discover Our Science →
</button>
</div>
</div>
</div>
</section>


{/* Product Excellence Showcase */}
<section id="revionyx" className="bg-white border-b border-gray-100 pt-16 pb-6 md:pt-20 md:pb-8">
  <div className="max-w-6xl mx-auto px-6">
    <div className="grid md:grid-cols-2 gap-6 items-start">
      
      {/* Headline */}
      <div className="md:col-span-2">
        <h2
          className="text-2xl md:text-3xl font-bold text-[#121212] leading-tight tracking-tight mb-2"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          What Makes Revionyx™<span className="text-[#baa573]"> Different</span>
        </h2>
      </div>

      {/* Full-width paragraph (no padding / no margin) */}
      <p className="md:col-span-2 text-base md:text-lg text-gray-600 leading-relaxed m-0 p-0">
        A premium longevity natural extracts born from years of cellular research:
        combining advanced science with uncompromising safety.
      </p>

      {/* Left column: features */}
      <div className="space-y-4 mt-4">
        <div className="flex items-start space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#baa573] to-[#baa573] rounded-full flex items-center justify-center flex-shrink-0">
            <i className="fas fa-flask text-white text-base"></i>
          </div>
          <div>
            <h4 className="text-lg font-bold text-[#121212]">Advanced Formulation</h4>
            <p className="text-gray-600 leading-relaxed">
              Revionyx™ represents years of research into longevity pathway, creating a proprietary blend unlike anything else in pet wellness.
            </p>
          </div>
        </div>

        <div className="flex items-start space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#baa573] to-[#baa573] rounded-full flex items-center justify-center flex-shrink-0">
            <i className="fas fa-shield-check text-white text-base"></i>
          </div>
          <div>
            <h4 className="text-lg font-bold text-[#121212]">Uncompromising Safety</h4>
            <p className="text-gray-600 leading-relaxed">
              Every ingredient is food-grade, ensuring your pet receives only the highest quality, safest compounds available.
            </p>
          </div>
        </div>

        <div className="flex items-start space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#baa573] to-[#baa573] rounded-full flex items-center justify-center flex-shrink-0">
            <i className="fas fa-spray-can text-white text-base"></i>
          </div>
          <div>
            <h4 className="text-lg font-bold text-[#121212]">Superior Delivery</h4>
            <p className="text-gray-600 leading-relaxed">
              Our powder delivery system ensures max bioavailability and stress-free administration: no struggling with pills.
            </p>
          </div>
        </div>
      </div>

{/* Right column: image + badges */}
<div className="relative mt-6 md:mt-2 md:scale-90 md:origin-top-right">
  <img
    src="together_ever.jpg"
    alt="Together Ever Premium Product"
    className="w-full mx-auto object-cover object-top rounded-2xl shadow-xl"
  />

  {/* Desktop-only floating badges */}
  <div className="hidden md:block">
    <div className="absolute -top-10 left-2 px-6 py-3 rounded-xl
      bg-[linear-gradient(135deg,rgba(255,249,225,0.55)_0%,rgba(243,215,137,0.35)_45%,rgba(226,193,97,0.45)_100%)]
      backdrop-blur-xl backdrop-saturate-150 border border-white/40
      shadow-[0_4px_18px_0_rgba(255,255,255,0.25),inset_0_1px_4px_rgba(255,255,255,0.15)]
      text-[#121212] max-w-[260px]"
    >
      <div className="flex items-center space-x-4">
        <span className="w-3 h-3 rounded-full bg-gradient-to-br from-[#C7A64C] to-[#D4B55A] flex-shrink-0"></span>
        <div>
          <div className="text-sm font-semibold tracking-wide">Premium Grade</div>
          <div className="text-xs text-[#3C3C3C]/80 font-medium">Laboratory Tested</div>
        </div>
      </div>
    </div>

    <div className="absolute -top-5 right-10 bg-gradient-to-r from-[#C7A64C] to-[#D4B55A] text-white px-4 py-2 rounded-lg shadow-md">
      <div className="text-center">
        <div className="text-sm font-bold">30-Day Supply</div>
        <div className="text-[10px] opacity-90">Powder Formula</div>
      </div>
    </div>
  </div>

  {/* Mobile-only: badges below image */}
  <div className="md:hidden mt-3 flex flex-col gap-2">
    <div className="px-4 py-2 rounded-xl bg-[#FFF9E1]/70 border border-[#C7A64C]/25 text-[#121212] shadow-sm">
      <div className="text-sm font-semibold">Premium Grade</div>
      <div className="text-xs opacity-80">Laboratory Tested</div>
    </div>
    <div className="px-4 py-2 rounded-xl bg-[#C7A64C] text-white shadow-sm">
      <div className="text-sm font-semibold">30-Day Supply</div>
      <div className="text-[11px] opacity-90">Powder Formula</div>
    </div>
  </div>
</div>
</div>
</div>
</section>


{/* Ingredient Spotlight */}
<section className="py-10 bg-white">
<div className="max-w-6xl mx-auto px-6">
<div className="text-center mb-4">
<h2
className="text-2xl md:text-3xl font-bold text-[#121212] mb-6 leading-tight tracking-tight"
style={{ fontFamily: 'Inter, sans-serif' }}
>
Inside Revionyx™
</h2>
<p className="text-lg text-gray-600 max-w-3xl mx-auto mb-4">
Our proprietary longevity complex: a science-driven natural extracts combination that supports your pet's processes of healthy aging, energy, and cellular metabolism
</p>
<div className="inline-flex items-center bg-gradient-to-r from-[#C7A64C]/10 to-[#D4B55A]/10 border border-[#C7A64C]/30 px-4 py-2 rounded-full mb-4">
<i className="fas fa-seedling text-[#C7A64C] mr-2"></i>
<span className="text-sm font-medium text-[#baa573] uppercase tracking-wide">Food-Grade • Safe</span>
</div>
</div>
<div className="bg-gradient-to-br from-[#C7A64C]/5 to-[#D4B55A]/5 p-4 rounded-xl border border-[#C7A64C]/20 mb-6">

<div className="grid md:grid-cols-3 gap-3">
  <div className="bg-white p-4 rounded-xl shadow-sm border border-[#C7A64C]/10">
<div className="w-16 h-16 bg-gradient-to-br from-[#acd7de] to-[#acd7de] rounded-full flex items-center justify-center mx-auto mb-6">
<i className="fas fa-balance-scale text-white text-lg"></i>
</div>
<h4 className="text-lg font-bold text-[#121212] text-center mb-4">Cellular Balance</h4>
<p className="text-gray-600 text-center leading-relaxed">
Supports the body's natural equilibrium at the cellular level, promoting optimal function and homeostasis.
</p>
</div>
<div className="bg-white p-4 rounded-xl shadow-sm border border-[#C7A64C]/10">
<div className="w-16 h-16 bg-gradient-to-br from-[#acd7de] to-[#acd7de] rounded-full flex items-center justify-center mx-auto mb-6">
<i className="fas fa-bolt text-white text-lg"></i>
</div>
<h4 className="text-lg font-bold text-[#121212] text-center mb-4">Natural Energy</h4>
<p className="text-gray-600 text-center leading-relaxed">
Enhances vitality through supporting the body's natural energy production pathways and metabolic processes.
</p>
</div>
<div className="bg-white p-4 rounded-xl shadow-sm border border-[#C7A64C]/10">
<div className="w-16 h-16 bg-gradient-to-br from-[#acd7de] to-[#acd7de] rounded-full flex items-center justify-center mx-auto mb-6">
<i className="fas fa-sync-alt text-white text-lg"></i>
</div>
<h4 className="text-lg font-bold text-[#121212] text-center mb-4">Cellular Maintenance</h4>
<p className="text-gray-600 text-center leading-relaxed">
Supports the body's natural maintenance processes, promoting healthy aging and cellular maintenance.
</p>
</div>
</div>
</div>
<div className="flex justify-center mt-4">
</div>

<RevionyxCompoundsShowcase contained={false}/>
</div>
</section>


{/* How to Use */}
<section id="usage" className="py-10 bg-[#F8F8F8]">
<div className="max-w-6xl mx-auto px-6">
<div className="text-center mb-8">
<h2
className="text-2xl md:text-3xl font-bold text-[#121212] mb-6 leading-tight tracking-tight"
style={{ fontFamily: 'Inter, sans-serif' }}
>
How to Use & Dosage Guide
</h2>
<p className="text-lg text-gray-600 max-w-3xl mx-auto">
Simple, stress-free administration with precise dosing for optimal longevity benefits
</p>
</div>

{/* Administration Methods */}
<div className="grid md:grid-cols-2 gap-10 items-center mb-12">
<div>
<h3 className="text-2xl font-bold text-[#121212] mb-8">Two Easy Ways to Give</h3>
<div className="space-y-8">
<div className="flex items-start space-x-4">
<div className="w-12 h-12 bg-[#baa573] rounded-full flex items-center justify-center flex-shrink-0">
<i className="fas fa-utensils text-white text-lg"></i>
</div>
<div>
<h4 className="text-lg font-bold text-[#121212] mb-2">Mix with Food</h4>
<p className="text-gray-600 leading-relaxed">
Spread directly onto your pet's wet or dry food. The flavorless formula won't affect taste, making mealtime easy and stress-free.
</p>
</div>
</div>
<div className="flex items-start space-x-4">
<div className="w-12 h-12 bg-[#acd7de] rounded-full flex items-center justify-center flex-shrink-0">
<i className="fas fa-tint text-white text-lg"></i>
</div>
<div>
<h4 className="text-lg font-bold text-[#121212] mb-2">Dissolve in Water</h4>
<p className="text-gray-600 leading-relaxed">
Gently pour directly into your pet's drinking water for maximum absorption. The pleasant taste makes direct dosing simple.
</p>
</div>
</div>
</div>
</div>
<div className="relative rounded-2xl overflow-hidden shadow-[0_12px_40px_rgba(0,0,0,0.1)] scale-90">
  <video
    ref={videoRef}
    src="powder_use.mp4"
    muted
    playsInline
    preload="none"
    loop
    controls={false}
    className="w-full h-full object-cover object-top rounded-2xl transition-opacity duration-700 opacity-100"
    onEnded={(e) => {
      const video = e.currentTarget;
      // fade out just before restarting
      video.classList.add('fade-out');
      setTimeout(() => {
        video.currentTime = 0;
        video.play().catch(() => {});
        video.classList.remove('fade-out');
      }, 500);
    }}
  />
</div>
</div>
</div>
</section>

{/* FAQ Section — Elegant, FDA-Compliant */}
<section className="py-10 bg-gradient-to-b from-[#FAFAFA] to-[#F3F3F3]">
  <div className="max-w-4xl mx-auto px-6">
    {/* Header */}
    <div className="text-center mb-6">
      <h2
        className="text-2xl md:text-3xl font-bold text-[#121212] mb-6 leading-tight tracking-tight"
        style={{ fontFamily: 'Inter, sans-serif' }}
      >
        Frequently Asked Questions
      </h2>
      <p className="text-lg md:text-lg text-gray-600 max-w-2xl mx-auto">
        General guidance for your peace of mind.
      </p>
    </div>

    {/* Accordion */}
    <div className="space-y-1">
      {[
        {
          q: "How soon can I expect to notice any changes?",
          a: "Every pet is unique. Together Ever™ is designed to support overall health aging and vitality over time. Many owners simply describe a gradual return of energy or brightness within several weeks of consistent use. Because this is a wellness supplement, individual responses will naturally vary, and no therapeutic or disease-related claims are made."
        },
        {
          q: "Is Together Ever™ safe for my dog or cat?",
          a: "Together Ever™ uses food-grade, Generally Recognized as Safe (GRAS) ingredients formulated under quality-controlled conditions. It is intended for healthy adult pets (1 year +). If your pet is pregnant, nursing, on medication, or has a chronic condition, please consult your veterinarian before introducing any supplement."
        },
        {
          q: "What’s the benefit of a powder format?",
          a: "Our powder is crafted for convenience and consistency. It can be mixed directly onto food or dissolve in water. This helps simplify daily routines and supports even dosing without any pills"
        },
        {
          q: "What makes Together Ever™ different from ordinary pet supplements?",
          a: "Together Ever™ is built on the Revionyx™ longevity complex—a proprietary blend inspired by cellular-health research. All components are food-grade and tested for purity. The formulation is designed to complement normal physiological processes rather than diagnose, treat, cure, or prevent disease."
        },
        {
          q: "Can Together Ever™ be used with other supplements or medications?",
          a: "Always discuss supplement combinations with your veterinarian. Because each pet’s metabolism and medications differ, professional guidance ensures compatibility and safety."
        }
      ].map((item, i) => (
        <details
          key={i}
          className="group bg-white/70 backdrop-blur-lg rounded-2xl shadow-[0_4px_24px_rgba(0,0,0,0.05)] border border-[#EAEAEA] overflow-hidden transition-all"
        >
          <summary className="flex items-center justify-between cursor-pointer px-5 py-3 select-none text-left">
            <h3 className="text-lg md:text-lg font-semibold text-[#121212] tracking-tight">
              {item.q}
            </h3>
            <span className="ml-4 inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#F2EBDD] text-[#C7A64C] transition-transform duration-300 group-open:rotate-45">
              +
            </span>
          </summary>
          <div className="px-5 pb-4 text-gray-700 leading-sung text-sm">
            {item.a}
          </div>
        </details>
      ))}
    </div>
  </div>
</section>



{/* Join Movement */}
<section className="py-12 bg-gradient-to-r from-[#acd7de] to-[#acd7de]">
<div className="max-w-4xl mx-auto px-6 text-center">
<h2
className="text-4xl font-bold text-white mb-6"
style={{ fontFamily: 'Playfair Display, serif' }}
>
Join the Longevity Movement
</h2>
<p className="text-lg text-white opacity-90 mb-8 max-w-2xl mx-auto">
Be among the first to give your pet the gift of extended, healthy years. Join thousands of pet families already on this journey.
</p>
<form onSubmit={handleEmailSubmit} className="max-w-md mx-auto">
<div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
<input
type="email"
value={email}
onChange={(e) => setEmail(e.target.value)}
placeholder="Enter your email"
className="flex-1 px-4 py-3 rounded-lg border-none text-[#121212] placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white text-sm"
required
/>
<button
type="submit"
className="bg-[#121212] text-white px-6 py-3 !rounded-button hover:bg-gray-800 transition-all duration-300 whitespace-nowrap cursor-pointer"
>
Get Early Access
</button>
</div>
</form>
<p className="text-white opacity-75 text-sm mt-4">
No spam, just longevity insights and early access to Together Ever™
</p>
</div>
</section>

{/* Footer */}
<footer className="py-16 bg-[#FAF9F5]">
<div className="max-w-6xl mx-auto px-6">
<div className="grid md:grid-cols-4 gap-8 mb-12">

<div className="space-y-4">
  <a href="/" className="inline-block">
    <img
      src="/logo.svg"
      alt="Together Ever"
      className="h-7 w-auto"
    />
  </a>

  <p className="text-[#121212] opacity-70 leading-relaxed max-w-xs">
    Support healthy aging in pets through longevity science
  </p>

  <p className="text-[#121212] opacity-70">
    support@togetherever.com
  </p>
</div>

<div>


</div>

<div>
<h4 className="font-bold text-[#121212] mb-4">Product</h4>
<ul className="space-y-2 text-[#121212] opacity-70">
<li><a href="#hero" className="hover:text-[#C7A64C] transition-colors cursor-pointer">How It Works</a></li>
<li><a href="#revionyx" className="hover:text-[#C7A64C] transition-colors cursor-pointer">Ingredients / Revionyx™</a></li>
<li><a href="#usage" className="hover:text-[#C7A64C] transition-colors cursor-pointer">Usage Guide</a></li>
<li><a href="/science#safety" className="hover:text-[#C7A64C] transition-colors cursor-pointer">Safety & Quality</a></li>
</ul>
</div>

<div>
<h4 className="font-bold text-[#121212] mb-4">Venture</h4>
<ul className="space-y-2 text-[#121212] opacity-70">
<li><a href="/about#value" className="hover:text-[#C7A64C] transition-colors cursor-pointer">Our Value</a></li>
<li><a href="/about#team" className="hover:text-[#C7A64C] transition-colors cursor-pointer">Our Team</a></li>
<li><a href="/science" className="hover:text-[#C7A64C] transition-colors cursor-pointer">Our Science</a></li>
</ul>
</div>

</div>
<div className="border-t border-gray-200 pt-8">
<div className="flex flex-col md:flex-row justify-between items-center">
<p className="text-[#121212] opacity-70 text-sm mb-4 md:mb-0">
© 2025 Together Ever™. All rights reserved.
</p>
<p className="text-[#121212] opacity-70 text-xs max-w-md text-center md:text-right">
*These statements have not been evaluated by the FDA. This product is not intended to diagnose, treat, cure, or prevent any disease.
</p>
</div>
</div>
</div>
</footer>

<style>{`
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=Inter:wght@400;500;600;700&display=swap');

/* Existing utilities */
.!rounded-button {
  border-radius: 8px;
}
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
input[type="number"] {
  -moz-appearance: textfield;
}

/* 🌎 Global scaling adjustments */
:root {
  --section-padding: 6rem;
  --heading-size: 2.25rem;
  --subheading-size: 1.125rem;
  --paragraph-size: 0.975rem;
}

/* Apply tighter section spacing */
section {
  padding-top: 3rem;
  padding-bottom: 3rem;
}

@media (min-width: 768px) {
  section {
    padding-top: 5rem;
    padding-bottom: 5rem;
  }
}

/* Typography scale down for better 100% zoom balance */
h1 {
  font-size: clamp(2.5rem, 4vw, 3.25rem);
  line-height: 1.15;
}
h2 {
  font-size: clamp(1.875rem, 3vw, 2.5rem);
  line-height: 1.2;
}
p, li {
  font-size: var(--paragraph-size);
  line-height: 1.65;
}

/* Featured On marquee */
.featured-fade {
  position: relative;
  overflow: hidden;
  width: 100%;
  mask-image: linear-gradient(to right, transparent, black 7%, black 93%, transparent);
  -webkit-mask-image: linear-gradient(to right, transparent, black 7%, black 93%, transparent);
}

.featured-marquee-track {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  width: max-content;
  gap: 0;
  animation: featured-marquee-scroll 32s linear infinite;
  will-change: transform;
}

.featured-fade:hover .featured-marquee-track {
  animation-play-state: paused;
}

.featured-logo-link {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
}

.featured-logo-item {
  height: 40px;
  padding: 0 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

.featured-logo-img {
  display: block;
  width: auto;
  max-width: auto;
  max-height: 24px;
  object-fit: contain;
  transition: transform 0.25s ease, opacity 0.25s ease;
}

.featured-logo-clickable {
  opacity: 0.96;
}

.featured-logo-clickable:hover {
  transform: scale(1.03);
}

.featured-logo-static {
  opacity: 0.96;
}

@media (min-width: 768px) {
  .featured-logo-item {
    height: 56px;
    width: 170px;
    padding: 0 18px;
  }

  .featured-logo-img {
    max-height: 26px;
  }
}

@keyframes featured-marquee-scroll {
  from {
    transform: translate3d(0, 0, 0);
  }
  to {
    transform: translate3d(-50%, 0, 0);
  }
}

`}</style>
</div>
);
};
export default App